import log from '@/lib/logger';
import { THEME_URL, TIMEZONES_URL } from '@/services/endpoints';
import { type AppDispatch, type RootState } from '@/store';
import { type Theme } from '@/types/swagger';
import { type UnknownAction } from '@reduxjs/toolkit';
import { http } from 'config';

import * as logoUrl from '../assets/images/enerlink-bajada-negativo.png';

interface SettingsState {
  path: string;
  width: number;
  locale: string;
  timezones: string[];
  theme: {
    logo: string;
    favicon: string;
    colors: Record<string, string>;
    background: string;
  };
}

// ----------------------------------- Actions ---------------------------------
// Action Types
const SWITCH_LANGUAGE = 'settings/switch_language';
const WINDOW_WIDTH = 'settings/window_width';
const LOAD_THEME = 'settings/load_theme';
const RESET_THEME = 'settings/reset_theme';
const TIMEZONES = 'settings/timezones';

// Action Creators
export const updateWindowWidth = (width: number) => ({
  type: WINDOW_WIDTH,
  payload: width,
});
export const loadTheme = (theme: Partial<Theme>) => ({ type: LOAD_THEME, payload: theme });
export const resetTheme = () => ({ type: RESET_THEME });

// ----------------------------------- Reducer ---------------------------------
// Init State
const INIT_STATE: SettingsState = {
  path: '',
  width: window.innerWidth,
  locale: 'es',
  timezones: [],
  theme: {
    logo: '',
    favicon: 'favicon.ico',
    colors: {
      'primary-darker': '#10244a',
      'primary-dark': '#2453ad',
      primary: '#3477f7',
      'primary-light': '#71a0f9',
      'primary-lighter': '#aec9fc',
      'secondary-darker': '#86002d',
      'secondary-dark': '#bd0055',
      secondary: '#f62681',
      'secondary-light': '#ff67b0',
      'secondary-lighter': '#ff9ee2',
      'bg-color-darkest': '#1a172d',
      'bg-color-darker': '#211d43',
      'bg-color-dark': '#272344',
      'bg-color': '#2b274b',
      'bg-color-light': '#403d5d',
      'bg-color-lighter': '#55526f',
      'bg-color-lightest': '#aaa9b7',
    },
    background: '#3477f7',
  },
};

// Reducer
const settingsReducer = (state = INIT_STATE, action: UnknownAction): SettingsState => {
  const { type, payload } = action;
  switch (type) {
    // case '@@router/LOCATION_CHANGE': {
    //   return { ...state, path: action.payload.location.pathname, navCollapsed: true };
    // }
    case WINDOW_WIDTH: {
      return { ...state, width: payload as number };
    }
    case SWITCH_LANGUAGE: {
      return { ...state, locale: payload as string };
    }
    case TIMEZONES: {
      return { ...state, timezones: payload as string[] };
    }
    case LOAD_THEME: {
      const theme = payload as { logo: string; favicon: string; colors: Record<string, string> };
      return {
        ...state,
        theme: {
          ...state.theme,
          logo: theme.logo || state.theme.logo,
          favicon: theme.favicon || state.theme.favicon,
          colors: {
            ...state.theme.colors,
            ...theme.colors,
          },
        },
      };
    }
    case RESET_THEME: {
      return {
        ...state,
        theme: {
          ...INIT_STATE.theme,
          logo: logoUrl.default,
        },
      };
    }
    default: {
      return state;
    }
  }
};

export default settingsReducer;

// Thunks
export const fetchTimezones = () => async (dispatch: AppDispatch) => {
  const apiTimezone = TIMEZONES_URL;
  return await http
    .get(apiTimezone)
    .then((response) => {
      if (response.data?.length) {
        const timezone = response.data;
        dispatch({ type: TIMEZONES, payload: timezone });
      }
      return response.data;
    })
    .catch((error) => {
      log.error(error);
      return error;
    });
};

// ? IDK why are we using this but could be useful for future reference
export const fetchTheme = () => async (dispatch: AppDispatch) => {
  const apiTheme = THEME_URL;
  return await http
    .get(apiTheme)
    .then((response) => {
      if (response?.data?.results?.length) {
        const theme = response.data.results.find((theme: Theme) => theme.active);
        const newTheme = {
          logo: theme.logo ? theme.logo : logoUrl.default,
          favicon: theme.favicon,
          colors: {
            'primary-darker': theme.css_header_text_color,
            'primary-dark': theme.css_header_background_color,
            primary: theme.logo_color,
            'primary-light': theme.css_header_link_color,
            'primary-lighter': theme.css_header_link_hover_color,
            'bg-color': theme.env_color,
            secondary: theme.title_color,
          },
        };
        dispatch(loadTheme(newTheme));
      }
      return response.data.results;
    })
    .catch((error) => {
      log.error(error);
      return error;
    });
};

export const getThemeSelector = (state: RootState) => state.settings.theme;
export const getLogoSelector = (state: RootState) => state.settings.theme.logo;
export const getTimezoneSelector = (state: RootState) => state.settings.timezones;
